// Family PIN Login Component
// Alternative login for family members (kids ages 5-12) using shared PIN
// No Cognito authentication - simple PIN validation

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { familyPINService } from '../../api/familyPINService';
import { FamilyMember, FamilyMemberSession, isValidPIN } from '../../types/familyMember';

interface FamilyPINLoginProps {
  onBack?: () => void;
}

const FamilyPINLogin: React.FC<FamilyPINLoginProps> = ({ onBack }) => {
  const navigate = useNavigate();

  // State
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [familyId, setFamilyId] = useState<string>('');
  const [step, setStep] = useState<'pin' | 'select'>('pin');

  const handlePINInput = (digit: string) => {
    if (pin.length < 4) {
      const newPin = pin + digit;
      setPin(newPin);

      // Auto-validate when 4 digits entered
      if (newPin.length === 4) {
        validatePIN(newPin);
      }
    }
  };

  const handlePINBackspace = () => {
    setPin(pin.slice(0, -1));
  };

  const validatePIN = async (pinToValidate: string) => {
    if (!isValidPIN(pinToValidate)) {
      toast.error('Please enter a 4-digit PIN');
      setPin('');
      return;
    }

    setLoading(true);

    try {
      const response = await familyPINService.validatePIN(pinToValidate);

      if (response.valid && response.members && response.members.length > 0) {
        // PIN is valid, show member selection
        setMembers(response.members);
        setFamilyId(response.family_id || '');
        setStep('select');
        toast.success('PIN accepted! Choose your name.');
      } else {
        // Invalid PIN
        toast.error(response.error_message || 'Incorrect PIN. Please try again.');
        setPin('');
      }
    } catch (error) {
      console.error('[FamilyPINLogin] Error validating PIN:', error);
      toast.error('Unable to validate PIN. Please try again.');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const handleMemberSelect = (member: FamilyMember) => {
    // Store family member session
    const session: FamilyMemberSession = {
      member_id: member.member_id,
      member_name: member.member_name,
      member_age: member.member_age,
      family_id: familyId,
      logged_in_at: new Date().toISOString()
    };

    sessionStorage.setItem('family_member_session', JSON.stringify(session));

    toast.success(`Welcome back, ${member.member_name}! 🎉`);

    // Navigate to kid task view
    navigate('/kid-tasks');
  };

  const handleBackToParentLogin = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/login');
    }
  };

  // Render PIN entry pad
  const renderPINPad = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-sm"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-400 rounded-3xl mb-4 shadow-lg">
          <Home className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Family PIN Login
        </h1>
        <p className="text-gray-600">Enter your family's 4-digit PIN</p>
      </div>

      {/* PIN Display */}
      <div className="flex justify-center gap-4 mb-8">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-bold transition-all ${
              pin.length > i
                ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white scale-110'
                : 'bg-gray-100 text-gray-300'
            }`}
          >
            {pin.length > i ? '•' : '-'}
          </div>
        ))}
      </div>

      {/* Number Pad */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
          <button
            key={num}
            onClick={() => handlePINInput(num.toString())}
            disabled={loading || pin.length === 4}
            className="h-20 bg-white border-2 border-gray-200 rounded-2xl text-3xl font-bold text-gray-900 hover:bg-purple-50 hover:border-purple-300 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {num}
          </button>
        ))}

        {/* Bottom row: Backspace, 0, Empty */}
        <button
          onClick={handleBackToParentLogin}
          className="h-20 bg-gray-100 border-2 border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-200 active:scale-95 transition-all flex items-center justify-center"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <button
          onClick={() => handlePINInput('0')}
          disabled={loading || pin.length === 4}
          className="h-20 bg-white border-2 border-gray-200 rounded-2xl text-3xl font-bold text-gray-900 hover:bg-purple-50 hover:border-purple-300 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
        >
          0
        </button>
        <button
          onClick={handlePINBackspace}
          disabled={loading || pin.length === 0}
          className="h-20 bg-gray-100 border-2 border-gray-200 rounded-2xl text-sm font-semibold text-gray-600 hover:bg-gray-200 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          ← Delete
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-purple-600" />
          <p className="text-gray-600 mt-2">Checking PIN...</p>
        </div>
      )}

      {/* Help Text */}
      <div className="text-center text-sm text-gray-500">
        Ask a parent for your family PIN
      </div>
    </motion.div>
  );

  // Render member selection
  const renderMemberSelection = () => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full max-w-2xl"
    >
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Who are you?
        </h1>
        <p className="text-gray-600">Choose your name to see your tasks</p>
      </div>

      {/* Member Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {members.map((member) => (
          <motion.button
            key={member.member_id}
            onClick={() => handleMemberSelect(member)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-purple-400 transition-all shadow-sm flex flex-col items-center gap-3"
          >
            {/* Avatar */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center text-4xl shadow-lg"
              style={{ backgroundColor: member.favorite_color || '#60A5FA' }}
            >
              {member.avatar_emoji || '😊'}
            </div>

            {/* Name */}
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900">{member.member_name}</h3>
              <p className="text-sm text-gray-500">Age {member.member_age}</p>
            </div>

            {/* Task Count Badge */}
            {member.assigned_task_count && member.assigned_task_count > 0 && (
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                {member.assigned_task_count} {member.assigned_task_count === 1 ? 'task' : 'tasks'}
              </div>
            )}
          </motion.button>
        ))}
      </div>

      {/* Back Button */}
      <button
        onClick={() => {
          setStep('pin');
          setPin('');
          setMembers([]);
        }}
        className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to PIN Entry
      </button>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center p-4">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full blur-3xl opacity-20 animate-float" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full blur-3xl opacity-20 animate-float" style={{ animationDelay: '2s' }} />
      </div>

      {/* Content */}
      <div className="relative z-10">
        <AnimatePresence mode="wait">
          {step === 'pin' ? renderPINPad() : renderMemberSelection()}
        </AnimatePresence>
      </div>

      {/* Decorative Elements */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(5deg);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default FamilyPINLogin;
